import { fmt, type Pt } from "@tasduvar/engine";
import { useProjectStore } from "../../state/projectStore";
import { useCadUi } from "../../state/cadUiStore";
import { closeSketch, parseSketchInput } from "../drawing/sketch";

const fmtPt = (p: Pt): string => `${fmt(p.x, 3)}; ${fmt(p.y, 3)}`;
export const SKETCH_PROMPT = "Nokta: x y · @dx dy · @L<açı · tıkla — K kapat · G geri · IPTAL";

/** CIZ kipini başlatır (seçili duvar taş/çokgen olmalı). */
export function startSketch(): { text: string; tone: "out" | "err" } {
  const project = useProjectStore.getState(), cad = useCadUi.getState();
  const wall = project.project?.walls.find((w) => w.id === project.selectedWallId);
  if (!wall) return { text: "Seçili duvar yok.", tone: "err" };
  if (wall.geometry.mode === "rc-cantilever") return { text: "Serbest kesit yalnız taş duvarda (DUVAR Taş).", tone: "err" };
  cad.setSketch({ points: [] }); cad.setLayout("model"); cad.setView("section"); cad.setPanMode(false);
  return { text: "CIZ — kesit köşelerini burundan başlayıp saat yönünün tersine girin (taban yatay). " + SKETCH_PROMPT, tone: "out" };
}

export function sketchAddPoint(p: Pt): void {
  const cad = useCadUi.getState(); const s = cad.sketch; if (!s) return;
  const points = [...s.points, p];
  cad.setSketch({ points });
  cad.pushHistory({ text: `Nokta ${points.length}: ${fmtPt(p)}${points.length >= 3 ? " — K ile kapatın" : ""}`, tone: "out" });
}
export function sketchUndo(): void {
  const cad = useCadUi.getState(); const s = cad.sketch; if (!s) return;
  cad.setSketch({ points: s.points.slice(0, -1) });
  cad.pushHistory({ text: s.points.length ? `Son nokta geri alındı (${s.points.length - 1} nokta)` : "Geri alınacak nokta yok", tone: "out" });
}
export function sketchCancel(): void {
  const cad = useCadUi.getState(); if (!cad.sketch) return;
  cad.setSketch(null); cad.pushHistory({ text: "CIZ iptal edildi.", tone: "out" });
}
/** Kapat: geçerliyse duvar çokgen kipine geçer; değilse hata basar, kip açık kalır. */
export function sketchClose(): void {
  const cad = useCadUi.getState(), project = useProjectStore.getState(); const s = cad.sketch; if (!s) return;
  const r = closeSketch(s.points);
  if ("errors" in r) { cad.pushHistory({ text: "Kesit kapatılamadı: " + r.errors.join("; "), tone: "err" }); return; }
  const wall = project.project?.walls.find((w) => w.id === project.selectedWallId);
  if (!wall) { cad.pushHistory({ text: "Seçili duvar yok.", tone: "err" }); cad.setSketch(null); return; }
  project.updateWall(wall.id, r.recipe);
  cad.setSketch(null); cad.setPropTab("geometry"); cad.setViewBox(null);
  const B = Math.max(...r.polygon.map((p) => p.x)), H = Math.max(...r.polygon.map((p) => p.y));
  cad.pushHistory({ text: `Kesit kapatıldı: ${r.polygon.length} köşe, B = ${fmt(B, 2)} m, H = ${fmt(H, 2)} m${r.bodyBaseY !== undefined ? `, temel üstü ${fmt(r.bodyBaseY, 2)} m` : ""} → hesap yenilendi`, tone: "ok" });
}
/** Komut satırı girdisi CIZ kipindeyken buraya gelir. */
export function sketchInput(text: string): void {
  const cad = useCadUi.getState(); const s = cad.sketch; if (!s) return;
  const a = parseSketchInput(text, s.points);
  if (a.kind === "error") cad.pushHistory({ text: a.msg, tone: "err" });
  else if (a.kind === "point") sketchAddPoint(a.p);
  else if (a.kind === "undo") sketchUndo();
  else if (a.kind === "cancel") sketchCancel();
  else sketchClose();
}
